/*
 * @providesModule TrophiesAheadToast
 * @format
 */

import React, {useEffect, useRef} from 'react';
import {View, Animated, TouchableOpacity, useColorScheme, Text} from 'react-native';
import styles from 'TrophiesAheadToast.style';
import SKZTrophy from 'SKZTrophy';
import SkillzImage from 'SkillzImage';
import AssetHelper from 'AssetHelper';
import SkillzSVG from 'SkillzSVG';
import LinearGradient from 'react-native-linear-gradient';
import Label from 'Label';
import {_m} from 'INTL';
import Sizes from 'Sizes';
import SkillzStyle from 'SkillzStyle';

// 🎨 Color Constants
const COLORS = {
  // Progress bar colors
  PROGRESS_FILL: '#851556',
  PROGRESS_FILL_DARK: '#320D61',

  // Background colors
  BACKGROUND_DARK: '#000000',
  BACKGROUND_LIGHT: '#FFFFFF',

  // Text colors
  TEXT_DARK: '#2C2C2C',
  TEXT_LIGHT: '#FFFFFF',

  // Claim button colors
  CLAIM_BUTTON_DARK: '#FF69B4',
  CLAIM_BUTTON_LIGHT: '#851556',

  // Progress bar unfilled colors
  PROGRESS_UNFILLED_LIGHT: '#FFE6F0',
  PROGRESS_UNFILLED_DARK: '#FFF0F5',

  // Background gradient colors
  BACKGROUND_GRADIENT: [
    'rgba(0, 67, 255, 0.15)',
    'rgba(9, 62, 255, 0.15)',
    'rgba(44, 46, 255, 0.15)',
    'rgba(70, 34, 255, 0.15)',
    'rgba(85, 27, 255, 0.15)',
    'rgba(91, 25, 255, 0.15)',
    'rgba(252, 0, 50, 0.15)',
  ],
} as const;

interface ITrophiesAheadToastProps {
  trophy?: SKZTrophy | null;
  onPress?: () => void;
  defaultTrophyIcon?: string;
}

export const TrophiesAheadToast: React.FC<ITrophiesAheadToastProps> = ({
  trophy,
  onPress,
  defaultTrophyIcon,
}) => {
  // 🎨 Theme detection
  const colorScheme = SkillzStyle.useValueForTheme('light', 'dark');
  const isDarkToast = colorScheme !== 'dark';
  // 🏆 Animation state for expanding the component
  const [isExpanded, setIsExpanded] = React.useState(false);

  // 🎯 Progress bar animation
  const [showProgress, setShowProgress] = React.useState(false);
  const [progressWidth, setProgressWidth] = React.useState(0);

  // 🎬 Smooth height and width animation
  const heightAnimation = useRef(new Animated.Value(60)).current;
  const widthAnimation = useRef(new Animated.Value(240)).current; // Start with initial width

  // 🎭 Track when animation is complete to show content smoothly
  const [animationComplete, setAnimationComplete] = React.useState(false);

  // 🕐 Timer refs to prevent readonly property errors
  const expandTimerRef = useRef<NodeJS.Timeout | null>(null);
  const progressTimerRef = useRef<NodeJS.Timeout | null>(null);

  // 🏆 Expand component after 2 seconds with smooth animation
  useEffect(() => {
    expandTimerRef.current = setTimeout(() => {
      setIsExpanded(true);

      // 🎬 Smooth height and width animation together
      Animated.parallel([
        Animated.timing(heightAnimation, {
          toValue: trophy?.state === 'AWARDED' ? 110 : 90, // Bigger height for awarded trophies
          duration: 400, // 400ms for smooth expansion
          useNativeDriver: false,
        }),
        Animated.timing(widthAnimation, {
          toValue: Sizes.screenWidth * 0.9,
          duration: 400, // 400ms for smooth expansion
          useNativeDriver: false,
        }),
      ]).start(() => {
        // 🎭 Animation complete, now safe to show content
        setAnimationComplete(true);
      });
    }, 2000);

    return () => {
      if (expandTimerRef.current) {
        clearTimeout(expandTimerRef.current);
        expandTimerRef.current = null;
      }
    };
  }, [heightAnimation]);

  // 🎯 Animate progress bar after expansion animation completes
  useEffect(() => {
    if (isExpanded && animationComplete) {
      // Wait 200ms after expansion completes then start progress animation
      progressTimerRef.current = setTimeout(() => {
        setShowProgress(true);
        // Animate progress from 0 to target value
        // Use the same logic as TrophyToastCell: (100 * parseFloat(total_progress)) / goal
        const targetProgress = trophy
          ? Math.max(0, Math.min((100 * parseFloat(trophy.total_progress)) / trophy.goal, 100))
          : 0;
        let currentProgress = 0;
        const increment = targetProgress / 16; // 16 steps for smooth animation

        const progressInterval = setInterval(() => {
          currentProgress += increment;
          if (currentProgress >= targetProgress) {
            currentProgress = targetProgress;
            clearInterval(progressInterval);
          }
          setProgressWidth(currentProgress);
        }, 25); // 25ms per step = 400ms total (faster animation)

        return () => clearInterval(progressInterval);
      }, 200); // Increased delay to 200ms after expansion completes

      return () => {
        if (progressTimerRef.current) {
          clearTimeout(progressTimerRef.current);
          progressTimerRef.current = null;
        }
      };
    }
  }, [isExpanded, animationComplete, trophy]);

  return (
    <TouchableOpacity activeOpacity={0.8} onPress={onPress} style={{width: '100%'}}>
      <Animated.View
        style={[
          styles.revampedTrophyToastContainer,
          {
            height: heightAnimation, // Smooth animated height
            width: widthAnimation, // Smooth animated width
            backgroundColor: isDarkToast ? COLORS.BACKGROUND_DARK : COLORS.BACKGROUND_LIGHT, // Black for dark theme, white for light
          },
        ]}
      >
        {/* Gradient Overlay */}
        <LinearGradient
          colors={COLORS.BACKGROUND_GRADIENT}
          locations={[0.07, 0.25, 0.63, 0.73, 0.85, 0.97, 1.0]}
          start={{x: 0, y: 0}}
          end={{x: 1, y: 1}}
          style={styles.gradientOverlay}
        />
        {/* 🏆 Initial "Trophies Ahead" Design */}
        <View style={isExpanded ? styles.expandedContent : styles.nonExpandedContent}>
          {/* Trophy Image Container */}
          <View style={isExpanded ? styles.expandedIconContainer : styles.nonExpandedIconContainer}>
            <SkillzImage
              source={
                trophy?.picture_url && trophy.picture_url.length > 0
                  ? trophy.picture_url
                  : defaultTrophyIcon ||
                    AssetHelper.pathForAssetNamed('ic_trophy_toast_default.png', false)
              }
              resizeMode={'contain'}
              style={isExpanded ? styles.expandedImage : styles.nonExpandedImage}
            />
          </View>

          {/* First 2 seconds: Simple "Trophies Ahead" text */}
          {!isExpanded && (
            <View style={styles.nonExpandedTextContainer}>
              <Label
                style={[
                  styles.revampedTrophyText,
                  {color: isDarkToast ? COLORS.TEXT_LIGHT : COLORS.TEXT_DARK},
                ]}
              >
                {_m('Trophies Ahead')}
              </Label>
            </View>
          )}

          {/* After 2 seconds: Quest-like notification using trophy properties */}
          {isExpanded && animationComplete && (
            <View style={styles.expandedContentRow}>
              <View style={styles.expandedTextContainer}>
                <Text
                  style={[
                    styles.revampedTrophyTextLine1,
                    {color: isDarkToast ? COLORS.TEXT_LIGHT : COLORS.TEXT_DARK},
                  ]}
                  numberOfLines={2}
                  allowFontScaling={false}
                  ellipsizeMode='tail'
                >
                  {_m(trophy?.description || '')}
                </Text>

                {showProgress && trophy?.state !== 'AWARDED' && (
                  <View style={styles.progressBarContainer}>
                    <View style={styles.progressBarBackground}>
                      <Animated.View
                        style={[
                          styles.progressBarFill,
                          {
                            width: `${progressWidth}%`,
                          },
                        ]}
                      >
                        <LinearGradient
                          colors={[COLORS.PROGRESS_FILL, COLORS.PROGRESS_FILL_DARK]}
                          start={{x: 0, y: 0}}
                          end={{x: 0, y: 1}}
                          style={styles.progressBarFillGradient}
                        />
                      </Animated.View>

                      {/* Unfilled Area with Light Pink Gradient */}
                      <LinearGradient
                        colors={[COLORS.PROGRESS_UNFILLED_LIGHT, COLORS.PROGRESS_UNFILLED_DARK]}
                        start={{x: 0, y: 0}}
                        end={{x: 0, y: 1}}
                        style={[
                          styles.progressBarUnfilled,
                          {
                            left: `${progressWidth}%`,
                          },
                        ]}
                      />
                    </View>
                  </View>
                )}

                {trophy?.state === 'AWARDED' && (
                  <TouchableOpacity
                    style={[
                      styles.claimButton,
                      {
                        backgroundColor: isDarkToast
                          ? COLORS.CLAIM_BUTTON_DARK
                          : COLORS.CLAIM_BUTTON_LIGHT, // Light pink for dark theme, dark purple for light theme
                      },
                    ]}
                    onPress={onPress}
                    activeOpacity={0.8}
                  >
                    <SkillzImage
                      source={AssetHelper.pathForAssetNamed('gift.png', false)}
                      style={styles.giftIcon}
                    />
                    <Label style={[styles.claimButtonText, {color: '#FFFFFF'}]}>
                      {_m('Claim Now')}
                    </Label>
                  </TouchableOpacity>
                )}
              </View>
              <SkillzSVG source={'chevron_right.svg'} style={styles.chevronIcon} />
            </View>
          )}
        </View>
      </Animated.View>
    </TouchableOpacity>
  );
};

export default TrophiesAheadToast;
